import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, TextField, Typography, Alert,
  MenuItem, Select, InputLabel, FormControl
} from '@mui/material';
import { submitUserProfile, fetchUserProfile, cacheProfile } from '../api/users';

const LATIN_NAME   = /^[A-Za-z \-']+$/;
const LATIN_STREET = /^[A-Za-z0-9 \-.,/\\]+$/;
const LATIN_CITY   = /^[A-Za-z \-'.]+$/;
const LATIN_ZIP    = /^[A-Za-z0-9\-]+$/;

function validateField(name, value) {
  if (!value) return null;
  switch (name) {
    case 'firstName':
    case 'lastName':
      return LATIN_NAME.test(value) ? null : 'latinOnly';
    case 'street':
      return LATIN_STREET.test(value) ? null : 'latinOnly';
    case 'city':
      return LATIN_CITY.test(value) ? null : 'latinOnly';
    case 'zip':
      return LATIN_ZIP.test(value) ? null : 'latinOnly';
    default:
      return null;
  }
}

export default function ProfilePage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    firstName: '', lastName: '', salutation: 'Herr',
    street: '', zip: '', city: '', countryCode: 'DE',
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    async function loadProfile() {
      try {
        const profile = await fetchUserProfile();
        if (profile?.profileComplete) {
          navigate('/catalog', { replace: true });
          return;
        }
        if (profile) {
          setForm(f => ({
            ...f,
            firstName: profile.firstName || f.firstName,
            lastName: profile.lastName || f.lastName,
            salutation: profile.salutation || f.salutation,
            street: profile.street || f.street,
            zip: profile.zip || f.zip,
            city: profile.city || f.city,
            countryCode: profile.countryCode || f.countryCode,
          }));
        }
      } catch {
        // leave form empty on error
      }
    }
    loadProfile();
  }, [navigate]);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors(fe => ({ ...fe, [name]: validateField(name, value) }));
    }
  }

  function handleBlur(e) {
    const { name, value } = e.target;
    setFieldErrors(fe => ({ ...fe, [name]: validateField(name, value) }));
  }

  function validateAll() {
    const fields = ['firstName', 'lastName', 'street', 'city', 'zip'];
    const errors = {};
    fields.forEach(name => {
      const err = validateField(name, form[name]);
      if (err) errors[name] = err;
    });
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!validateAll()) return;
    setError('');
    setLoading(true);
    try {
      await submitUserProfile(form);
      const profile = await fetchUserProfile();
      cacheProfile(profile);
      navigate('/catalog');
    } catch (err) {
      if (err.response?.status === 409) {
        const profile = await fetchUserProfile();
        cacheProfile(profile);
        if (profile.profileComplete) {
          navigate('/catalog');
        } else {
          setError(t('profile.conflictError'));
        }
      } else {
        setError(err.response?.data?.error || err.message || 'Submission failed');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 480, mx: 'auto', mt: 6, p: 3 }}>
      <Typography variant="h5" mb={1}>{t('profile.title')}</Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        {t('profile.subtitle')}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {t('profile.latinHint')}
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>{t('profile.salutation')}</InputLabel>
        <Select name="salutation" value={form.salutation} label={t('profile.salutation')} onChange={handleChange}>
          <MenuItem value="Herr">{t('profile.herr')}</MenuItem>
          <MenuItem value="Frau">{t('profile.frau')}</MenuItem>
        </Select>
      </FormControl>
      <TextField label={t('profile.firstName')} name="firstName" fullWidth required
        value={form.firstName} onChange={handleChange} onBlur={handleBlur} sx={{ mb: 2 }}
        error={!!fieldErrors.firstName} helperText={fieldErrors.firstName ? t(`profile.${fieldErrors.firstName}`) : ''} />
      <TextField label={t('profile.lastName')} name="lastName" fullWidth required
        value={form.lastName} onChange={handleChange} onBlur={handleBlur} sx={{ mb: 2 }}
        error={!!fieldErrors.lastName} helperText={fieldErrors.lastName ? t(`profile.${fieldErrors.lastName}`) : ''} />
      <TextField label={t('profile.street')} name="street" fullWidth required
        value={form.street} onChange={handleChange} onBlur={handleBlur} sx={{ mb: 2 }}
        error={!!fieldErrors.street} helperText={fieldErrors.street ? t(`profile.${fieldErrors.street}`) : ''} />
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField label={t('profile.zip')} name="zip" required
          value={form.zip} onChange={handleChange} onBlur={handleBlur} sx={{ width: 120 }}
          error={!!fieldErrors.zip} helperText={fieldErrors.zip ? t(`profile.${fieldErrors.zip}`) : ''} />
        <TextField label={t('profile.city')} name="city" fullWidth required
          value={form.city} onChange={handleChange} onBlur={handleBlur}
          error={!!fieldErrors.city} helperText={fieldErrors.city ? t(`profile.${fieldErrors.city}`) : ''} />
      </Box>
      <TextField label={t('profile.countryCode')} name="countryCode" fullWidth required
        value={form.countryCode} onChange={handleChange} sx={{ mb: 3 }} />
      <Button type="submit" variant="contained" fullWidth disabled={loading}>
        {loading ? t('profile.saving') : t('profile.save')}
      </Button>
    </Box>
  );
}
